import { useContext } from "react";
import { Banner, TextArea } from "@hireart/component-library";
import PropTypes from "prop-types";
import { UserContext } from "common/context/user-context";
import { ProjectOptionShape, ShiftShape } from "common/utils/timesheet";
import { BreakManager } from "./sub-components/BreakManager";
import { ManagerOverrideInfo } from "./sub-components/ManagerOverrideInfo";
import { ReferenceNumberInput } from "./sub-components/ReferenceNumberInput";
import { ShiftDetailTotal } from "./sub-components/ShiftDetailTotal";
import { ShiftTagSelector } from "./sub-components/ShiftTagSelector";
import { ShiftTimeSection } from "./sub-components/ShiftTimeSection";

export const ShiftContent = ({
  shift,
  editingShift,
  shiftDate,
  dayTotalHours,
  selectedProjectToken = null,
  projectOptions,
  timezones,
  validationErrors,
  isReadOnly,
  onProjectChange,
  onStartTimeChange,
  onEndTimeChange,
  onTimezoneChange = undefined,
  onBreakChange,
  onRemoveBreak,
  onAddBreak,
  onReferenceNumberChange,
  getCurrentTimezone,
  overrideReasonRequired = false,
  overrideNotes = "",
  onOverrideNotesChange,
}) => {
  const user = useContext(UserContext);
  const usesShiftsReferenceNumber = user?.usesShiftsReferenceNumber || false;

  return (
    <div
      className="body-2 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-16"
      style={{
        paddingBottom: "24px",
      }}
    >
      {/* Manager Override Information Display */}
      {shift.hoursWorkedEmployer && <ManagerOverrideInfo shift={shift} />}

      {/* Shift Assignment */}
      <ShiftTagSelector
        selectedProjectToken={selectedProjectToken}
        isReadOnly={isReadOnly}
        projectOptions={projectOptions}
        onProjectChange={onProjectChange}
      />

      {/* Shift Schedule - Start and End Times */}
      <ShiftTimeSection
        shift={editingShift}
        shiftDate={shiftDate}
        isReadOnly={isReadOnly}
        currentTimezone={getCurrentTimezone()}
        timezones={timezones}
        onStartTimeChange={onStartTimeChange}
        onEndTimeChange={onEndTimeChange}
        onTimezoneChange={onTimezoneChange}
      />

      {/* Reference Number Input - Only show if feature is enabled */}
      {usesShiftsReferenceNumber && (
        <ReferenceNumberInput
          value={editingShift.referenceNumber}
          isReadOnly={isReadOnly}
          onReferenceNumberChange={onReferenceNumberChange}
          className="mb-10"
        />
      )}

      {/* Breaks Section */}
      <BreakManager
        shiftId={editingShift.id}
        breaks={editingShift.breaks}
        isReadOnly={isReadOnly}
        hoursBreakEmployee={editingShift.hoursBreakEmployee}
        shiftDate={shiftDate}
        onBreakChange={onBreakChange}
        onRemoveBreak={onRemoveBreak}
        onAddBreak={onAddBreak}
      />

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="flex flex-col gap-y-2">
          {validationErrors.map((error) => (
            <Banner key={`error-${error}`} variant="error">
              <div className="body-2 mb-1 font-semibold">Error detected</div>
              <div className="body-2">{error}</div>
            </Banner>
          ))}
        </div>
      )}

      {/* Override Reason - only shown when required (manager-created shifts) */}
      {overrideReasonRequired && (
        <div className="flex flex-col gap-2">
          <label className="body-2 font-semibold text-neutral-90">
            Override reason <span className="text-red-80">*</span>
          </label>
          <TextArea
            value={overrideNotes}
            onChange={(e) => onOverrideNotesChange?.(e.target.value)}
            placeholder="Explain why you're adding this shift..."
            rows={3}
            className="w-full"
            size="md"
            aria-label="Override reason"
            aria-required
          />
        </div>
      )}

      {/* Shift Total Summary */}
      <ShiftDetailTotal shift={editingShift} dayTotalHours={dayTotalHours} />
    </div>
  );
};
ShiftContent.propTypes = {
  shift: ShiftShape.isRequired,
  editingShift: ShiftShape.isRequired,
  shiftDate: PropTypes.instanceOf(Date).isRequired,
  dayTotalHours: PropTypes.number.isRequired,
  selectedProjectToken: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  projectOptions: PropTypes.arrayOf(ProjectOptionShape).isRequired,
  timezones: PropTypes.arrayOf(
    PropTypes.shape({
      tzname: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      utc_offset: PropTypes.string.isRequired,
    })
  ).isRequired,
  validationErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onProjectChange: PropTypes.func.isRequired,
  onStartTimeChange: PropTypes.func.isRequired,
  onEndTimeChange: PropTypes.func.isRequired,
  onTimezoneChange: PropTypes.func,
  onBreakChange: PropTypes.func.isRequired,
  onRemoveBreak: PropTypes.func.isRequired,
  onAddBreak: PropTypes.func.isRequired,
  onReferenceNumberChange: PropTypes.func,
  getCurrentTimezone: PropTypes.func.isRequired,
  overrideReasonRequired: PropTypes.bool,
  overrideNotes: PropTypes.string,
  onOverrideNotesChange: PropTypes.func,
};
